import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { hrApi } from '@/services/api/hr'
import type {
  CostBearerRow, DeductionKind, HrFinanceLessonRow, HrLessonRowStatus, PayoutKind,
} from '@/types/hr'
import { extractErrorMessage } from '@/utils/errorHandler'
import { formatHrDate, isModuleInactiveError } from '@/utils/hrLabels'
import { HrModuleInactiveNotice } from '@/components/hr/HrModuleInactiveNotice'
import { FloatInput } from '@/components/ui/FloatInput'

function fmt(n: number): string {
  return n.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const STATUS_PILL: Record<HrLessonRowStatus, string> = {
  default: 'bg-green-100 text-green-700',
  wahl: 'bg-indigo-100 text-indigo-700',
  override: 'bg-slate-100 text-slate-700',
  limit: 'bg-red-100 text-red-700',
}
const STATUS_LABEL: Record<HrLessonRowStatus, string> = {
  default: 'Default', wahl: 'Wahl', override: 'Override', limit: 'Limit',
}

interface Draft {
  payout: PayoutKind
  deduction: DeductionKind
  bearer: string
  amount: number | null
}

export function HRAbrechnungstabellePage() {
  const [rows, setRows] = useState<HrFinanceLessonRow[]>([])
  const [total, setTotal] = useState(0)
  const [bearers, setBearers] = useState<CostBearerRow[]>([])
  const [qInput, setQInput] = useState('')
  const [q, setQ] = useState('')
  const [week, setWeek] = useState<'this' | 'all'>('this')
  const [payer, setPayer] = useState('')
  const [status, setStatus] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [drafts, setDrafts] = useState<Record<string, Draft>>({})
  const [force, setForce] = useState(false)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [moduleInactive, setModuleInactive] = useState(false)
  const didInitSelect = useRef(false)
  const filterKeyRef = useRef('')

  const fetchRows = useCallback(async () => {
    const filterKey = `${q}|${week}|${payer}|${status}`
    if (filterKeyRef.current !== filterKey) {
      filterKeyRef.current = filterKey
      didInitSelect.current = false
    }
    try {
      const data = await hrApi.getLessons({
        q: q || undefined,
        week: week === 'this' ? 'this' : undefined,
        payer: payer || undefined,
        status: status || undefined,
        limit: 500,
      })
      setRows(data.lessons)
      setTotal(data.total)
      setModuleInactive(false)
      setError(null)
      setSelected(prev => {
        const allowed = new Set(data.lessons.filter(r => r.status !== 'limit').map(r => r.lesson_id))
        if (!didInitSelect.current) {
          didInitSelect.current = true
          return allowed
        }
        const next = new Set<string>()
        for (const id of prev) if (allowed.has(id) || data.lessons.some(r => r.lesson_id === id)) next.add(id)
        return next
      })
    } catch (e: unknown) {
      if (isModuleInactiveError(e)) setModuleInactive(true)
      else setError(extractErrorMessage(e, { loadError: 'Tabelle konnte nicht geladen werden' }))
    } finally { setLoading(false) }
  }, [q, week, payer, status])

  useEffect(() => {
    const t = window.setTimeout(() => setQ(qInput), 300)
    return () => window.clearTimeout(t)
  }, [qInput])
  useEffect(() => { void fetchRows() }, [fetchRows])
  useEffect(() => {
    hrApi.getCostBearers().then(r => setBearers(r.cost_bearers)).catch(() => {})
  }, [])

  const draftOf = (row: HrFinanceLessonRow): Draft => drafts[row.lesson_id] ?? {
    payout: row.default_payout,
    deduction: row.requester_deduction ?? 'ipb',
    bearer: '',
    amount: row.amount_default ?? 1,
  }

  const patchDraft = (id: string, row: HrFinanceLessonRow, patch: Partial<Draft>) => {
    setDrafts(d => ({ ...d, [id]: { ...draftOf(row), ...patch } }))
  }

  const nonLimit = rows.filter(r => r.status !== 'limit')
  const allNonLimitSelected = nonLimit.length > 0 && nonLimit.every(r => selected.has(r.lesson_id))
  const someNonLimit = nonLimit.some(r => selected.has(r.lesson_id))
  const selectedRows = rows.filter(r => selected.has(r.lesson_id))
  const limitSelected = selectedRows.some(r => r.status === 'limit')
  const selectedAmount = selectedRows.reduce((s, r) => s + (draftOf(r).amount ?? r.amount_default ?? 1), 0)

  const toggleAll = () => {
    if (allNonLimitSelected) setSelected(new Set())
    else setSelected(new Set(nonLimit.map(r => r.lesson_id)))
  }

  const toggle = (row: HrFinanceLessonRow) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(row.lesson_id)) next.delete(row.lesson_id)
      else next.add(row.lesson_id)
      return next
    })
  }

  const verbuchen = async () => {
    if (selectedRows.length === 0) return
    if (limitSelected && !force) return
    setBusy(true); setError(null); setNotice(null)
    try {
      if (selectedRows.some(r => {
        const a = draftOf(r).amount
        return a != null && a <= 0
      })) {
        setError('Betrag muss positiv sein')
        return
      }
      const items = selectedRows.map(r => {
        const d = draftOf(r)
        const item: {
          lesson_id: string
          payout?: PayoutKind
          requester_deduction?: DeductionKind
          cost_bearer_id?: string
          amount?: number
        } = { lesson_id: r.lesson_id }
        if (d.payout !== r.default_payout) item.payout = d.payout
        if (r.payer === 'private' && d.deduction !== (r.requester_deduction ?? 'ipb')) {
          item.requester_deduction = d.deduction
        }
        if (d.bearer) item.cost_bearer_id = d.bearer
        const defaultAmt = r.amount_default ?? 1
        if (d.amount != null && d.amount !== defaultAmt) item.amount = d.amount
        return item
      })
      const r = await hrApi.settleLessons(items, force)
      const parts = [`${r.settled_lessons} Lektion(en) verbucht.`]
      if (r.skipped.length) parts.push(`${r.skipped.length} übersprungen.`)
      if (r.failed.length) parts.push(`${r.failed.length} fehlgeschlagen.`)
      setNotice(parts.join(' '))
      setSelected(new Set())
      setForce(false)
      await fetchRows()
    } catch (e: unknown) {
      setError(extractErrorMessage(e, { loadError: 'Abrechnung fehlgeschlagen' }))
    } finally { setBusy(false) }
  }

  const canSubmit = selectedRows.length > 0 && (!limitSelected || force)

  const headerState = useMemo(() => {
    if (allNonLimitSelected) return true
    if (someNonLimit) return 'mixed'
    return false
  }, [allNonLimitSelected, someNonLimit])

  if (moduleInactive) return <HrModuleInactiveNotice title="Abrechnungstabelle" />
  if (loading) return <div className="p-6 text-gray-500">Laden...</div>

  return (
    <div className="flex flex-col min-h-full">
      <div className="flex-1 min-h-0 overflow-auto">
        <div className="bg-white h-full flex flex-col">
          <div className="px-4 py-2.5 border-b flex gap-2 text-xs items-center flex-wrap shrink-0">
            <input value={qInput} onChange={e => setQInput(e.target.value)}
              className="border rounded px-2 py-1 min-w-[180px]" placeholder="Suchen Fach, Name, Klasse…" />
            <select value={week} onChange={e => setWeek(e.target.value as 'this' | 'all')}
              className="border rounded px-2 py-1 bg-white">
              <option value="this">Diese Woche</option>
              <option value="all">Alle offenen</option>
            </select>
            <select value={payer} onChange={e => setPayer(e.target.value)}
              className="border rounded px-2 py-1 bg-white">
              <option value="">Alle Zahler</option>
              <option value="private">Privat</option>
              <option value="school">Schule</option>
            </select>
            <select value={status} onChange={e => setStatus(e.target.value)}
              className="border rounded px-2 py-1 bg-white">
              <option value="">Alle Status</option>
              <option value="default">Default</option>
              <option value="wahl">Selbst-Wahl</option>
              <option value="override">Override</option>
              <option value="limit">Limit</option>
            </select>
            <span className="ml-auto text-gray-500">
              {total} Zeilen{total > rows.length ? ' (erste 500)' : ''} · {selected.size} ausgewählt
            </span>
          </div>
          {error && <div className="px-4 py-2 bg-red-100 text-red-700 text-sm">{error}</div>}
          {notice && <div className="px-4 py-2 bg-green-100 text-green-700 text-sm">{notice}</div>}
          <div className="flex-1 overflow-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-500 uppercase sticky top-0">
                <tr>
                  <th className="px-2 py-2">
                    <input type="checkbox" checked={headerState === true}
                      ref={el => { if (el) el.indeterminate = headerState === 'mixed' }}
                      onChange={toggleAll} aria-label="Alle Nicht-Limit auswählen" />
                  </th>
                  <th className="text-left px-2 py-2">Datum</th>
                  <th className="text-left px-2 py-2">Lektion</th>
                  <th className="text-left px-2 py-2">Vertreter</th>
                  <th className="text-left px-2 py-2">Abwesend</th>
                  <th className="text-left px-2 py-2">IPB Vertr.</th>
                  <th className="text-left px-2 py-2">Gutschrift</th>
                  <th className="text-left px-2 py-2">Abbuchung</th>
                  <th className="text-left px-2 py-2">Träger</th>
                  <th className="text-left px-2 py-2">Lekt.</th>
                  <th className="text-left px-2 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.length === 0 && (
                  <tr><td colSpan={11} className="px-4 py-6 text-center text-gray-500">Keine offenen Lektionen</td></tr>
                )}
                {rows.map(row => {
                  const d = draftOf(row)
                  return (
                    <tr key={row.lesson_id} className={row.status === 'limit' ? 'bg-red-50' : ''}>
                      <td className="px-2 py-1.5">
                        <input type="checkbox" checked={selected.has(row.lesson_id)}
                          onChange={() => toggle(row)} aria-label={row.subject_name} />
                      </td>
                      <td className="px-2 whitespace-nowrap">
                        {formatHrDate(row.date)}{row.start_time ? ` ${row.start_time}` : ''}
                      </td>
                      <td className="px-2 font-medium">{row.subject_name}{row.class_name && row.class_name !== '—' ? ` ${row.class_name}` : ''}</td>
                      <td className="px-2">{row.substitute_name}</td>
                      <td className="px-2">{row.requester_name}</td>
                      <td className={`px-2 ${row.status === 'limit' ? 'text-red-600' : row.substitute_saldo < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {row.status === 'limit' ? 'Limit' : `${row.substitute_saldo > 0 ? '+' : ''}${fmt(row.substitute_saldo)}`}
                      </td>
                      <td className="px-2">
                        <select value={d.payout} onChange={e => patchDraft(row.lesson_id, row, { payout: e.target.value as PayoutKind })}
                          className="border rounded px-1 py-0.5 bg-white">
                          <option value="lesson_account">IPB</option>
                          <option value="paid_out">Auszahlung</option>
                        </select>
                      </td>
                      <td className="px-2">
                        {row.payer !== 'private' ? (
                          <span className="text-gray-400">—</span>
                        ) : (
                          <select value={d.deduction} onChange={e => patchDraft(row.lesson_id, row, { deduction: e.target.value as DeductionKind })}
                            className="border rounded px-1 py-0.5 bg-white">
                            <option value="ipb">IPB</option>
                            <option value="salary">Lohn</option>
                          </select>
                        )}
                      </td>
                      <td className="px-2">
                        <select value={d.bearer} onChange={e => patchDraft(row.lesson_id, row, { bearer: e.target.value })}
                          className="border rounded px-1 py-0.5 bg-white">
                          <option value="">IPB</option>
                          {bearers.filter(b => b.is_active).map(b => (
                            <option key={b.id} value={b.id}>{b.name}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2">
                        <FloatInput value={d.amount} allowEmpty
                          onChange={v => patchDraft(row.lesson_id, row, { amount: v })}
                          className="w-14 border rounded px-1 py-0.5" />
                      </td>
                      <td className="px-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full font-semibold ${STATUS_PILL[row.status]}`}>
                          {STATUS_LABEL[row.status]}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2.5 border-t bg-indigo-50 flex items-center gap-3 text-sm shrink-0">
            <span className="font-medium">{selected.size} ausgewählt · {fmt(selectedAmount)} Lekt.</span>
            <span className="text-xs text-gray-500">
              {limitSelected ? 'Force nötig für ausgewählte Limit-Zeilen' : 'Limit-Zeilen sind nicht vorausgewählt'}
            </span>
            {limitSelected && (
              <label className="text-xs inline-flex items-center gap-1.5">
                <button type="button" role="switch" aria-checked={force} aria-label="Force"
                  onClick={() => setForce(f => !f)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full ${force ? 'bg-amber-600' : 'bg-gray-300'}`}>
                  <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white ${force ? 'translate-x-[18px]' : 'translate-x-1'}`} />
                </button>
                Force
              </label>
            )}
            <button type="button" disabled={busy || !canSubmit} onClick={() => void verbuchen()}
              className="ml-auto text-xs px-3 py-1.5 bg-indigo-600 text-white rounded-md disabled:opacity-50">
              Ausgewählte verbuchen
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HRAbrechnungstabellePage
